import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { X, ArrowLeft } from 'lucide-react';
import { DetalleContext, type Entidad } from './contexto-detalle';
import { cn } from '@/lib/cn';

/**
 * PANEL DE DETALLE — se apila, y la tabla de fondo no se pierde.
 *
 * Maciel: *«que una cosa me lleve a la otra»*. Abre un producto, desde ahí una
 * venta donde aparece, desde ahí el cliente. Tres niveles, con un «atrás»
 * DENTRO del panel, y la tabla sigue ahí atrás esperando.
 *
 * ═══ POR QUÉ UNA PILA Y NO UNA PANTALLA NUEVA ═══
 *
 * Navegar a otra pantalla obliga a volver con el botón del navegador y
 * reconstruir dónde estabas: qué filtro tenías, en qué fila ibas. Con la pila,
 * el contexto nunca se pierde — cerrar devuelve exactamente al sitio.
 *
 * ═══ EN EL CELULAR OCUPA TODA LA PANTALLA ═══
 *
 * Un panel de 480 px en un teléfono es una rendija. Abajo de `sm` se lleva el
 * ancho completo; arriba, se queda a la derecha y deja ver la tabla.
 */

/**
 * Envuelve la tabla y provee el abridor a todo su árbol:
 *
 *     <ConDetalle render={(e) => <Detalle entidad={e} />}>
 *       <DataTable … />
 *     </ConDetalle>
 */
export function ConDetalle({
  render,
  children,
}: {
  render: (entidad: Entidad) => ReactNode;
  children: ReactNode;
}) {
  const [pila, setPila] = useState<Entidad[]>([]);

  const abrir = useCallback((e: Entidad) => {
    setPila((prev) => {
      const ultimo = prev[prev.length - 1];
      if (ultimo && ultimo.tipo === e.tipo && ultimo.id === e.id) return prev;
      return [...prev, e];
    });
  }, []);
  const atras = useCallback(() => setPila((p) => p.slice(0, -1)), []);
  const cerrar = useCallback(() => setPila([]), []);

  useEffect(() => {
    if (pila.length === 0) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        atras();
      }
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [pila.length, atras]);

  return (
    <DetalleContext.Provider value={{ abrir }}>
      {children}
      <DrawerPila pila={pila} onAtras={atras} onCerrar={cerrar}>
        {render}
      </DrawerPila>
    </DetalleContext.Provider>
  );
}

function DrawerPila({
  pila,
  onAtras,
  onCerrar,
  children,
}: {
  pila: Entidad[];
  onAtras: () => void;
  onCerrar: () => void;
  children: (e: Entidad) => ReactNode;
}) {
  const arriba = pila[pila.length - 1];
  if (!arriba) return null;

  return (
    <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-modal="true">
      <button
        type="button"
        aria-label="Cerrar"
        onClick={onCerrar}
        className="absolute inset-0 bg-slate-900/30 backdrop-blur-[1px]"
      />

      <aside
        className={cn(
          'relative flex h-full w-full flex-col bg-surface shadow-2xl',
          'sm:w-[min(32rem,100vw)]',
          'animate-in slide-in-from-right duration-150',
        )}
      >
        <header className="flex items-center gap-2 border-b border-surface-border px-4 py-3">
          {pila.length > 1 && (
            <button
              type="button"
              onClick={onAtras}
              title={`Volver a ${pila[pila.length - 2]?.etiqueta ?? 'atrás'}`}
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}

          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
              {arriba.etiqueta}
            </p>
            {arriba.detalle && (
              <p className="truncate text-xs text-slate-500">{arriba.detalle}</p>
            )}
          </div>

          {/* Migas de la pila: se ve de dónde se viene sin tener que recordarlo. */}
          {pila.length > 1 && (
            <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500 dark:bg-slate-800">
              {pila.length}
            </span>
          )}

          <button
            type="button"
            onClick={onCerrar}
            title="Cerrar (Esc)"
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4">{children(arriba)}</div>
      </aside>
    </div>
  );
}

/** Una sección del panel. Para que todos los detalles se vean igual. */
export function SeccionDetalle({
  titulo,
  accion,
  children,
}: {
  titulo: string;
  accion?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="mb-5">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{titulo}</h3>
        {accion}
      </div>
      {children}
    </section>
  );
}

/** Par etiqueta/valor. Los valores numéricos van tabulares y a la derecha. */
export function DatoDetalle({
  etiqueta,
  children,
  numerico,
}: {
  etiqueta: string;
  children: ReactNode;
  numerico?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 text-sm">
      <span className="shrink-0 text-slate-500">{etiqueta}</span>
      <span
        className={cn(
          'min-w-0 text-right text-slate-900 dark:text-slate-100',
          numerico && 'tabular-nums',
        )}
      >
        {children}
      </span>
    </div>
  );
}
